import {
    DEFAULT_OG_IMAGE,
    OG_LOCALE,
    SITE_NAME,
} from './constants';
import { absoluteUrl } from './json-ld';

const MANAGED_ATTR = 'data-seo-managed';

function createManagedMeta(attributes) {
    const el = document.createElement('meta');
    el.setAttribute(MANAGED_ATTR, 'true');
    Object.entries(attributes).forEach(([key, value]) => {
        el.setAttribute(key, value);
    });
    document.head.appendChild(el);
}

function clearManagedSeoNodes() {
    document.querySelectorAll(`[${MANAGED_ATTR}="true"]`).forEach((node) => node.remove());
}

/**
 * Apply page-level SEO tags (title, meta, canonical, OG, Twitter, JSON-LD).
 * Removes previously managed tags on each call to avoid duplicates after route changes.
 *
 * @param {Object} config
 * @param {string} config.title
 * @param {string} [config.description]
 * @param {string} [config.canonical]
 * @param {string} [config.robots='index,follow']
 * @param {string} [config.ogImage]
 * @param {string} [config.ogType='website']
 * @param {Object|Object[]|null} [config.jsonLd]
 */
export function applyPageSeo(config = {}) {
    clearManagedSeoNodes();

    const {
        title,
        description = '',
        canonical = '',
        robots = 'index,follow',
        ogImage = DEFAULT_OG_IMAGE,
        ogType = 'website',
        ogImageAlt = '',
        ogImageWidth = '',
        ogImageHeight = '',
        jsonLd = null,
    } = config;

    if (title) {
        document.title = title;
    }

    if (description) {
        createManagedMeta({ name: 'description', content: description });
    }

    if (robots) {
        createManagedMeta({ name: 'robots', content: robots });
    }

    const ogTitle = title || document.title;
    const ogUrl = canonical || (typeof window !== 'undefined' ? window.location.href : '');

    const absoluteImage = absoluteUrl(ogImage || DEFAULT_OG_IMAGE);
    const imageType = /\.jpe?g(\?|$)/i.test(absoluteImage)
        ? 'image/jpeg'
        : /\.png(\?|$)/i.test(absoluteImage)
            ? 'image/png'
            : /\.webp(\?|$)/i.test(absoluteImage)
                ? 'image/webp'
                : '';

    createManagedMeta({ property: 'og:title', content: ogTitle });
    createManagedMeta({ property: 'og:description', content: description });
    createManagedMeta({ property: 'og:type', content: ogType });
    createManagedMeta({ property: 'og:url', content: ogUrl });
    createManagedMeta({ property: 'og:image', content: absoluteImage });
    createManagedMeta({ property: 'og:image:secure_url', content: absoluteImage });
    if (imageType) createManagedMeta({ property: 'og:image:type', content: imageType });
    if (ogImageWidth) createManagedMeta({ property: 'og:image:width', content: String(ogImageWidth) });
    if (ogImageHeight) createManagedMeta({ property: 'og:image:height', content: String(ogImageHeight) });
    if (ogImageAlt) createManagedMeta({ property: 'og:image:alt', content: ogImageAlt });
    createManagedMeta({ property: 'og:site_name', content: SITE_NAME });
    createManagedMeta({ property: 'og:locale', content: OG_LOCALE });

    createManagedMeta({ name: 'twitter:card', content: 'summary_large_image' });
    createManagedMeta({ name: 'twitter:title', content: ogTitle });
    createManagedMeta({ name: 'twitter:description', content: description });
    createManagedMeta({ name: 'twitter:image', content: absoluteImage });
    if (ogImageAlt) createManagedMeta({ name: 'twitter:image:alt', content: ogImageAlt });

    if (canonical) {
        const link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', canonical);
        link.setAttribute(MANAGED_ATTR, 'true');
        document.head.appendChild(link);
    }

    if (jsonLd) {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute(MANAGED_ATTR, 'true');
        script.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(script);
    }
}
